import React, { useState, useEffect, useMemo } from 'react';
import { MapPin } from '@phosphor-icons/react';
import { useLocation } from '../context/LocationContext';
import PlaceCard from '../components/PlaceCard';
import PlaceCardSkeleton from '../components/PlaceCardSkeleton';
import AdCard from '../components/AdCard';
import EmptyState from '../components/EmptyState';
import ContactFooter from '../components/ContactFooter';
import telegramService from '../services/telegram';
import { getCategories, getTags, getAds } from '../services/api';
import { sortPlacesByDistance } from '../services/geolocation';
import { useFilteredPlaces } from '../hooks/useFilteredPlaces';

const NearbyPage = () => {
  const { userLocation, hasLocation, requestUserLocation, loading: locationLoading, getDistanceToPlace, locationPermission } = useLocation();

  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ads, setAds] = useState([]);

  const { places, loading: placesLoading } = useFilteredPlaces(
    activeCategoryId,
    selectedTagIds,
    userLocation,
    30
  );

  // Сохранение и восстановление позиции скролла
  useEffect(() => {
    const currentPath = window.location.pathname;
    const savedPosition = sessionStorage.getItem(`scroll-${currentPath}`);

    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition));
      }, 100);
    }

    const handleScroll = () => {
      sessionStorage.setItem(`scroll-${currentPath}`, window.scrollY.toString());
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRequestLocation = async () => {
    try {
      await requestUserLocation();
    } catch (error) {
      // Handle location request error silently
    }
  };

  // Load categories and tags
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [categoriesRes, tagsRes, adsRes] = await Promise.all([
          getCategories(),
          getTags(),
          getAds(null, 'nearby'),
        ]);

        const loadedCategories = categoriesRes.data || [];
        const loadedTags = tagsRes.data || [];
        const loadedAds = (adsRes.data || []).sort((a, b) => (a.nearbyOrder || 0) - (b.nearbyOrder || 0));

        setCategories(loadedCategories);
        setTags(loadedTags);
        setAds(loadedAds);

        // Set first category as active by default
        if (loadedCategories.length > 0) {
          setActiveCategoryId(loadedCategories[0].id);
        }
      } catch (error) {
        // Handle loading error silently
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Get tags for active category
  const categoryTags = useMemo(() => {
    if (!activeCategoryId) return [];

    return tags.filter(tag => {
      const tagCategories = tag.categories || [];
      return tagCategories.some(cat => {
        const catId = cat.id || cat;
        return catId === activeCategoryId;
      });
    });
  }, [tags, activeCategoryId]);

  // Toggle tag
  const toggleTag = (tagId) => {
    telegramService.hapticFeedback('light');

    setSelectedTagIds(prev => {
      if (prev.includes(tagId)) {
        return prev.filter(id => id !== tagId);
      } else {
        return [...prev, tagId];
      }
    });

    // Auto-scroll to center the selected tag
    setTimeout(() => {
      const button = document.querySelector(`button[data-tag-id="${tagId}"]`);
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 50);
  };

  // Switch category
  const switchCategory = (categoryId) => {
    telegramService.hapticFeedback('light');
    setActiveCategoryId(categoryId);
    setSelectedTagIds([]);

    // Auto-scroll to center the selected category
    setTimeout(() => {
      const button = document.querySelector(`button[data-category-id="${categoryId}"]`);
      if (button) {
        button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 50);
  };

  // Sort places by distance and limit to 30 per category
  const sortedPlaces = useMemo(() => {
    if (!places.length) return [];

    // Sort by distance if we have user location
    let sorted = places;
    if (userLocation) {
      sorted = sortPlacesByDistance(
        places,
        userLocation.latitude,
        userLocation.longitude
      );
    }

    // Limit to 30 places per category
    return sorted.slice(0, 30);
  }, [places, userLocation]);

  return (
    <div className="min-h-screen bg-theme-bg pb-6">
      {/* Заголовок честен к состоянию: без доступа список идёт в своём
          порядке, а не по близости — обещать «Рядом с вами» нечем */}
      <section
        className="px-4 pb-4"
        style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
      >
        <h1 className="text-h2 text-theme-text-primary">
          {hasLocation() ? 'Рядом с вами' : 'Места на карте'}
        </h1>
      </section>

      {/* Content */}
      <div className="px-4">
        {/* Location prompt - when no location */}
        {!hasLocation() && locationPermission !== 'denied' && (
          <div className="mb-4 rounded-lg border border-theme-border bg-theme-card p-6 text-center">
            <div className="mb-3 flex justify-center">
              <div className={`flex h-16 w-16 items-center justify-center rounded-full bg-theme-card-secondary ${
                locationLoading ? 'animate-pulse' : ''
              }`}>
                <MapPin size={28} weight="light" className="text-theme-text-hint" />
              </div>
            </div>
            <p className="mb-1 text-h3 text-theme-text-primary">
              Геолокация не предоставлена
            </p>
            <p className="mb-4 text-body-sm text-theme-text-secondary">
              Разрешите доступ, чтобы увидеть ближайшие места
            </p>
            <button
              onClick={handleRequestLocation}
              disabled={locationLoading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-colors duration-fast ease-out active:scale-98 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <MapPin size={20} />
              <span>{locationLoading ? 'Получаем геолокацию…' : 'Разрешить доступ'}</span>
            </button>
          </div>
        )}

        {/* Location denied - show instructions */}
        {!hasLocation() && locationPermission === 'denied' && (
          <div className="mb-4 rounded-lg border border-theme-border bg-theme-card p-6 text-center">
            <div className="mb-3 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-theme-card-secondary">
                <MapPin size={28} weight="light" className="text-theme-text-hint" />
              </div>
            </div>
            <p className="mb-1 text-h3 text-theme-text-primary">
              Доступ к геолокации запрещён
            </p>
            {/* Инструкция из трёх шагов свёрнута в одну фразу: третий пункт
                был не действием пользователя, а обещанием приложения */}
            <p className="mb-4 text-body-sm text-theme-text-secondary">
              Нажмите «Попробовать снова» и разрешите доступ во всплывающем окне
            </p>
            {/* Повтор обычного действия, а не опасного: красный по правилу
                2.1 означает удаление */}
            <button
              onClick={handleRequestLocation}
              disabled={locationLoading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-md border-0 bg-action text-label text-on-action transition-colors duration-fast ease-out active:scale-98 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Попробовать снова
            </button>
          </div>
        )}

        {/* Пояснение и «Обновить» одной строкой: две служебные полосы
            во всю ширину раньше отодвигали ленту на пол-экрана */}
        {hasLocation() && (
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="flex items-center gap-1.5 text-caption text-theme-text-secondary">
              <MapPin size={14} className="flex-shrink-0" />
              Сначала ближайшие
            </p>
            <button
              onClick={handleRequestLocation}
              disabled={locationLoading}
              className="flex-shrink-0 border-0 bg-transparent text-caption font-semibold text-action-text transition-colors duration-fast ease-out disabled:opacity-50"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {locationLoading ? 'Обновляем…' : 'Обновить'}
            </button>
          </div>
        )}

        {/* Category Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
          {categories.map(category => (
            <button
              key={category.id}
              data-category-id={category.id}
              onClick={() => switchCategory(category.id)}
              /* Выбранная категория синяя, как и выбранный тег ниже: лайм —
                 акцент бренда, а не состояние выбора (брендбук 2.4).
                 Два языка для одного смысла на одном экране */
              className={`flex h-[40px] items-center gap-2 whitespace-nowrap rounded-md px-4 transition-colors duration-fast ease-out ${
                activeCategoryId === category.id
                  ? 'bg-action text-on-action'
                  : 'bg-theme-card-secondary text-theme-text-primary'
              }`}
            >
              {category.icon?.url && (
                <div className="w-5 h-5 flex-shrink-0">
                  <img
                    src={category.icon.url}
                    alt={category.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              )}
              <span className="text-body-sm font-medium">
                {category.name}
              </span>
            </button>
          ))}
        </div>

        {/* Tag Filters */}
        {categoryTags.length > 0 && (
          <div className="overflow-x-auto mb-4 -mx-4 px-4" style={{ scrollbarWidth: 'none' }}>
            <div className="flex gap-2 pb-2">
              {categoryTags.map(tag => (
                <button
                  key={tag.id}
                  data-tag-id={tag.id}
                  onClick={() => toggleTag(tag.id)}
                  className={`flex h-[36px] items-center gap-2 whitespace-nowrap rounded-md px-3 transition-colors duration-fast ease-out ${
                    selectedTagIds.includes(tag.id)
                      ? 'bg-action'
                      : 'bg-theme-card-secondary'
                  }`}
                >
                  {tag.icon?.url && (
                    <img
                      src={tag.icon.url}
                      alt={tag.name}
                      className="w-4 h-4 flex-shrink-0"
                    />
                  )}
                  <span className={`flex-shrink-0 text-body-sm font-medium leading-tight ${
                    selectedTagIds.includes(tag.id) ? 'text-on-action' : 'text-theme-text-primary'
                  }`}>
                    {tag.name}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Скелетоны вместо спиннера: сетка не схлопывается в точку
            при каждой смене категории */}
        {(loading || placesLoading) && (
          <div className="grid grid-cols-2 gap-4">
            {[0, 1, 2, 3].map(i => <PlaceCardSkeleton key={i} />)}
          </div>
        )}

        {/* Places grid */}
        {!loading && !placesLoading && sortedPlaces.length > 0 && (
          <div className="grid grid-cols-2 gap-4">
            {(() => {
              const result = [];
              const adShowCounts = {};
              let nextAdIndex = 0;
              let placesUntilNextAd = ads.length > 0 ? (ads[0].nearbyShowEveryNPlaces || 3) : 0;

              ads.forEach(ad => {
                adShowCounts[ad.id] = 0;
              });

              sortedPlaces.forEach((place, placeIndex) => {
                result.push(
                  <PlaceCard
                    key={place.id}
                    place={place}
                    showDistance={hasLocation()}
                    distance={hasLocation() ? getDistanceToPlace(place) : null}
                  />
                );

                if (ads.length > 0) {
                  placesUntilNextAd--;

                  if (placesUntilNextAd === 0 && nextAdIndex < ads.length) {
                    const currentAd = ads[nextAdIndex];

                    if (adShowCounts[currentAd.id] < (currentAd.nearbyRepeatCount || 1)) {
                      result.push(<AdCard key={`ad-${currentAd.id}-${adShowCounts[currentAd.id]}`} ad={currentAd} source="nearby" />);
                      adShowCounts[currentAd.id]++;
                      placesUntilNextAd = currentAd.nearbyShowEveryNPlaces || 3;
                    }

                    if (adShowCounts[currentAd.id] >= (currentAd.nearbyRepeatCount || 1)) {
                      nextAdIndex++;
                      if (nextAdIndex < ads.length) {
                        placesUntilNextAd = ads[nextAdIndex].nearbyShowEveryNPlaces || 3;
                      }
                    }
                  }
                }
              });

              return result;
            })()}
          </div>
        )}

        {/* Empty state */}
        {!loading && !placesLoading && sortedPlaces.length === 0 && (
          <EmptyState
            icon={MapPin}
            title="Нет мест поблизости"
            description="Попробуйте другую категорию или снимите фильтры"
            // Кнопка только когда есть что сбрасывать: иначе она обещает
            // действие, которое ничего не изменит
            actionLabel={selectedTagIds.length > 0 ? 'Сбросить фильтры' : undefined}
            onAction={selectedTagIds.length > 0 ? () => setSelectedTagIds([]) : undefined}
          />
        )}

        {/* Contact Footer */}
        <ContactFooter />
      </div>
    </div>
  );
};

export default NearbyPage;
